import React from 'react';
import { Spacing } from '../../../../../components/entities-card/Spacing';
import { LegislationCardTitleLink } from '../../../../../legislation/card/LegislationCardTitleLink';
import { EntitiesCardBody } from '../../../../../components/entities-card/EntitiesCardBody';
import { DecisionQpc } from '../../../../../decision/legislation-snippet/qpc/DecisionQpc';
import { DecisionReferences } from '../../../../../decision/legislation-snippet/references/DecisionReferences';
import { doctrineAnalytics } from '../../../../../utils/analyticsUtils';
import { buildUrlWithParameters } from '../../../../../utils/url-utils/urlUtils';
import { fetchLawCardReferenceDecisions } from './searchSnippetLegislation.api';
import { LegislationContent } from './LegislationContent';
import { LegislationRestrictor } from './LegislationRestrictor';
import { LegislationSelector } from './LegislationSelector';
import { Connect, PropsFromRedux } from './LegislationSnippetContainer';
import { makeLawEventData } from './legislationSnippetUtils';
import {
  withRestrictedModal,
  WithRestrictedModalProps,
} from '../../../../../components/modal/components/modal-restricted/withRestrictedModal';
import { UserContextType } from '../../../../../user/user.types';
import { withUser } from '../../../../../user/UserContext';
import { StateType } from '../../../../../state/state.types';
import { DecisionReferenceInSnippet } from '../../../../../decision/decision.types';
import { SearchMainSnippetCard } from '../SearchMainSnippetCard';

/* eslint-disable camelcase */
interface CustomWindow extends Window {
  original_query?: {
    source: string;
  };
}
/* eslint-enable camelcase */

declare let window: CustomWindow;

type Props = {
  legislation: NonNullable<StateType['results']['snippets']['law']>;
  originalQueryKey?: string;
} & UserContextType &
  PropsFromRedux &
  PropsFromRedux &
  WithRestrictedModalProps;

type State = {
  decisionsReferences: Array<DecisionReferenceInSnippet>;
  selectedLawArticleId: string;
};

class RawLegislationSnippet extends React.Component<Props, State> {
  state: State = {
    decisionsReferences: [],
    // Select the restricted version else the most recent
    selectedLawArticleId: this.props.legislation.otherVersions.reduce(
      (prev, current) =>
        this.props.restrictToLawArticleIds.includes(current.articleId)
          ? current.articleId
          : prev,
      this.props.legislation.articleId,
    ),
  };

  componentDidMount() {
    const { legislation } = this.props;
    const lawForAnalytics = {
      ...makeLawEventData(legislation),
      source: window.original_query ? window.original_query.source : 'direct',
    };

    doctrineAnalytics('Search LawDisplayed', lawForAnalytics);
    this.loadDecisionReferences();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.legislation.articleId !== this.props.legislation.articleId) {
      this.loadDecisionReferences();
    }
  }

  onTitleClick = () => {
    const { accessPremium, legislation, openRestrictedModal } = this.props;

    if (!accessPremium) {
      openRestrictedModal('search_law');
    } else {
      doctrineAnalytics('Search LawClicked', makeLawEventData(legislation));
    }
  };

  onSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value: articleId } = event.target;
    const {
      accessPremium,
      openRestrictedModal,
      onFilterOnArticle,
    } = this.props;

    if (!accessPremium) {
      openRestrictedModal('restrict_law_version');
    } else {
      doctrineAnalytics('Search ChangeLawVersionClicked', {
        reportOriginalQueryKey: true,
      });
      const previousArticleId = this.state.selectedLawArticleId;
      this.setState({
        selectedLawArticleId: articleId,
      });
      onFilterOnArticle(previousArticleId, false);
      onFilterOnArticle(articleId, true);
    }
  };

  restrictSearchToLawVersion = () => {
    const {
      accessPremium,
      onFilterOnArticle,
      openRestrictedModal,
      restrictToLawArticleIds,
    } = this.props;

    if (!accessPremium) {
      openRestrictedModal('restrict_law_version');
    } else {
      const toRestrict = !restrictToLawArticleIds?.includes(
        this.state.selectedLawArticleId,
      );
      doctrineAnalytics('Search RestrictLawClicked', {
        restricted: toRestrict,
        reportOriginalQueryKey: true,
      });
      onFilterOnArticle(this.state.selectedLawArticleId, toRestrict);
    }
  };

  loadDecisionReferences = async () => {
    const { legislation, originalQueryKey } = this.props;
    const { data } = await fetchLawCardReferenceDecisions(
      legislation.articleId,
    );
    if (data.refdecisions) {
      const refDecisionsWithUrl = data.refdecisions.map(decision => ({
        ...decision,
        url: buildUrlWithParameters(decision.referenceUrl, {
          original_query_key: originalQueryKey,
        }),
      }));
      this.setState({
        decisionsReferences: refDecisionsWithUrl,
      });
    }
  };

  render() {
    const { decisionsReferences } = this.state;
    const {
      restrictToLawArticleIds,
      legislation,
      accessPremium,
      searchQueryUrl,
    } = this.props;

    const lawUrl = accessPremium
      ? buildUrlWithParameters(`${legislation.url}?${searchQueryUrl}`, {
          source: 'law-snippet',
        })
      : undefined;
    return (
      <SearchMainSnippetCard>
        <EntitiesCardBody>
          <LegislationCardTitleLink onClick={this.onTitleClick} href={lawUrl}>
            {legislation.title}
          </LegislationCardTitleLink>
          <Spacing />
          {legislation.otherVersions.length > 0 && (
            <>
              <LegislationSelector
                lawOtherVersions={legislation.otherVersions}
                selected={this.state.selectedLawArticleId}
                onSelect={this.onSelect}
              />
              <LegislationRestrictor
                onCheck={this.restrictSearchToLawVersion}
                checked={
                  !!restrictToLawArticleIds?.includes(
                    this.state.selectedLawArticleId,
                  )
                }
              />
            </>
          )}
          <LegislationContent law={legislation} />

          <DecisionQpc
            source="search_law_snippet"
            articleId={legislation.articleId}
          />
          {decisionsReferences.length > 0 && (
            <DecisionReferences
              accessPremium={accessPremium}
              source="search_law_snippet"
              decisions={decisionsReferences}
            />
          )}
        </EntitiesCardBody>
      </SearchMainSnippetCard>
    );
  }
}

export const LegislationSnippet = Connect(
  withUser(withRestrictedModal(RawLegislationSnippet)),
);
